'use client';
import React from 'react';
import { IRepository } from '@/types';
import { usePathname } from 'next/navigation';
import * as Icon from '@/ui/Icon';
import { RocketIcon, ReaderIcon } from '@radix-ui/react-icons';
import { Config } from '@/hooks/useConfig';
import * as Styled from './styled';
import Tag from '@/ui/Tag';
import { useWindowSize } from '@uidotdev/usehooks';
import useAuthorization from '@/hooks/useAuthorization';

import DropdownNav from './DropDownNav';

type RepositoryTabType =
  | 'data'
  | 'branches'
  | 'commits'
  | 'merges'
  | 'compare'
  | 'schemas'
  | 'about'
  | 'users'
  | 'settings'
  | 'collaborate'
  | 'workspaces'
  | 'evaluations'
  | 'notebooks';

export type TNavOption = {
  name: string;
  path: string;
  icon: React.ReactNode;
  label: string;
  count?: number;
};

const LinkTab: React.FC<{
  href: string;
  disabled?: boolean;
  isActive?: boolean;
  hideMobile?: boolean;
  children: React.ReactNode;
}> = ({ href, disabled, isActive, hideMobile, children }) => {
  const pathname = usePathname();
  return (
    <Styled.LinkTab href={href} disabled={disabled} $active={isActive || href === pathname} $hideMobile={hideMobile}>
      {children}
    </Styled.LinkTab>
  );
};

const NavLinkTab: React.FC<{
  option?: TNavOption;
  activeTab?: string;
  hideMobile?: boolean;
  disabled?: boolean;
}> = ({ option, activeTab, hideMobile, disabled }) => {
  if (!option) {
    console.error('NavLinkTab requies a valid option prop');
    return null;
  }
  return (
    <LinkTab href={option.path} isActive={activeTab === option.name} hideMobile={hideMobile} disabled={disabled}>
      {option.icon}
      <span>{option.label}</span>
      {!!option.count && (
        <Tag pill small>
          {option.count}
        </Tag>
      )}
    </LinkTab>
  );
};

type TNavOptions = {
  [Key in RepositoryTabType]?: TNavOption;
};

export const getNavOptions = (repository: IRepository, mergeRequestCount?: number, compareCount?: number) => {
  const baseRepositoryPath = `/${repository.namespace}/${repository.name}`;
  const dataPath = baseRepositoryPath;
  const branchesPath = `${baseRepositoryPath}/branches`;
  const mergeRequestsPath = `${baseRepositoryPath}/merges`;
  const settingsPath = `${baseRepositoryPath}/settings`;
  const comparePath = `${baseRepositoryPath}/compare`;
  const collaboratePath = `${baseRepositoryPath}/collaborate`;
  const workspacesPath = `${baseRepositoryPath}/workspaces`;
  const evaluationsPath = `${baseRepositoryPath}/evaluations`;
  const notebooksPath = `${baseRepositoryPath}/notebooks`;
  const navOptions: TNavOptions = {
    data: { name: 'data', path: dataPath, icon: <Icon.Database />, label: 'Data' },
    branches: { name: 'branches', path: branchesPath, icon: <Icon.Branch />, label: 'Branches' },
    merges: {
      name: 'merges',
      path: mergeRequestsPath,
      icon: <Icon.Merge />,
      label: 'Merge Requests',
      count: mergeRequestCount,
    },
    // compare: { name: 'compare', path: comparePath, icon: <Icon.Compare />, label: 'Compare', count: compareCount },
    collaborate: { name: 'collaborate', path: collaboratePath, icon: <Icon.Users />, label: 'Collaborate' },
    // workspaces: { name: 'workspaces', path: workspacesPath, icon: <LayersIcon />, label: 'Workspaces' },
    evaluations: { name: 'evaluations', path: evaluationsPath, icon: <RocketIcon />, label: 'Evaluations' },
    notebooks: { name: 'notebooks', path: notebooksPath, icon: <ReaderIcon />, label: 'Notebooks' },
    settings: { name: 'settings', path: settingsPath, icon: <Icon.Cog />, label: 'Settings' },
  };

  return navOptions;
};

const RepositoryNavTabs: React.FC<{
  navOptions: ReturnType<typeof getNavOptions>;
  activeTab: RepositoryTabType;
  mergeRequestsEnabled: boolean;
  compareEnabled?: boolean;
  collaborateTabEnabled?: boolean;
  workspacesEnabled?: boolean;
  evaluationsEnabled?: boolean;
  settingsEnabled?: boolean;
  notebooksEnabled?: boolean;
}> = ({
  navOptions,
  activeTab,
  mergeRequestsEnabled = false,
  compareEnabled = false,
  collaborateTabEnabled = false,
  workspacesEnabled = false,
  evaluationsEnabled = false,
  settingsEnabled = false,
  notebooksEnabled = false,
}) => {
  const { width } = useWindowSize();

  if (!mergeRequestsEnabled) {
    delete navOptions.merges;
  }

  if (!settingsEnabled) {
    delete navOptions.settings;
  }

  if (!compareEnabled) {
    delete navOptions.compare;
  }
  if (!collaborateTabEnabled) {
    delete navOptions.collaborate;
  }
  if (!workspacesEnabled) {
    delete navOptions.workspaces;
  }
  if (!evaluationsEnabled) {
    delete navOptions.evaluations;
  }
  if (!notebooksEnabled) {
    delete navOptions.notebooks;
  }

  const arrNavOptions = Object.keys(navOptions).map((k) => navOptions[k as RepositoryTabType]);

  const maxTabWidth = 145;
  const visibleTabCount = width ? Math.floor(width / maxTabWidth) : arrNavOptions.length;
  const visibleNavOptions = arrNavOptions.slice(0, visibleTabCount);
  const hiddenNavOptions = arrNavOptions.slice(visibleTabCount);

  return (
    <Styled.RepositoryNavTabs>
      {visibleNavOptions.map((option) => (
        <NavLinkTab key={option?.name} option={option} activeTab={activeTab} />
      ))}
      {!!hiddenNavOptions?.length && <DropdownNav options={hiddenNavOptions as TNavOption[]} />}
    </Styled.RepositoryNavTabs>
  );
};

export const WrappedRepositoryNavTabs: React.FC<{
  repository: IRepository;
  mergeRequestCount?: number;
  compareCount?: number;
}> = ({ repository, mergeRequestCount, compareCount }) => {
  const pathName = usePathname();
  const { config } = Config.useContainer();

  const auth = useAuthorization();
  const repoAuthorization = auth.forRepository(repository);

  const mergeRequestsEnabled = !!config?.merge_requests_enabled && repoAuthorization.canEdit();
  const compareEnabled = !!config?.compare_enabled;
  const collaborateTabEnabled = false;
  const workspacesEnabled = !!config?.workspaces_enabled && repoAuthorization.canEdit();
  const evaluationsEnabled = !!config?.evaluations_enabled;
  const settingsEnabled = repoAuthorization.canViewSettings();
  const notebooksEnabled = !!config?.notebooks_enabled;
  const pathSegment = pathName?.split('/')[3];
  const navOptions = getNavOptions(repository, mergeRequestCount, compareCount);
  const activeTab = (navOptions[pathSegment as RepositoryTabType]?.name || 'data') as RepositoryTabType;

  return (
    <RepositoryNavTabs
      navOptions={navOptions}
      activeTab={activeTab}
      mergeRequestsEnabled={mergeRequestsEnabled}
      compareEnabled={compareEnabled}
      collaborateTabEnabled={collaborateTabEnabled}
      workspacesEnabled={workspacesEnabled}
      evaluationsEnabled={evaluationsEnabled}
      settingsEnabled={settingsEnabled}
      notebooksEnabled={notebooksEnabled}
    />
  );
};

export default RepositoryNavTabs;
